import { yupResolver } from "@hookform/resolvers/yup";
import { EyeIcon, EyeOffIcon } from "assets/icons";
import { toastServerError } from "helpers";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { useRefetchQuery } from "libs/reactQuery";
import { useGetVariantsByIds } from "modules/products";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Button, LoaderScreen } from "ui";

import {
  useCreatePack,
  useGetPack,
  useReplacePackSlots,
  useTogglePackActive,
  useUpdatePack
} from "../../apis";
import { PackFormHeader, PackSlotsEditor } from "../../components";
import {
  EMPTY_PACK_FORM_VALUES,
  PACK_QUERY_KEY,
  PACKS_QUERY_KEY
} from "../../constants";
import {
  convertPackFormDataToCreateRequestBody,
  convertPackFormDataToUpdateRequestBody,
  getEditablePackFormValues,
  handlePackError,
  packSchema
} from "../../helpers";
import { PackFormValues } from "../../packs.types";

import styles from "../../components/PackForm/PackForm.module.scss";

const PackDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { packId } = useParams();

  const isEdit = !!packId && packId !== "new";
  const refetchList = useRefetchQuery([PACKS_QUERY_KEY]);
  const refetchPack = useRefetchQuery([PACK_QUERY_KEY, packId ?? ""]);

  const { pack, isLoading: isPackLoading } = useGetPack(
    isEdit ? packId : undefined
  );

  const toggleActiveMutation = useTogglePackActive({
    onSuccess: () => {
      void refetchPack();
      void refetchList();
    },
    onError: toastServerError
  });

  const variantIds = useMemo(() => {
    if (!pack) return [];
    const all = pack.slots.flatMap((slot) =>
      slot.options.map((option) => option.productVariantId)
    );
    return Array.from(new Set(all));
  }, [pack]);

  const { variants: hydratedVariants, isLoading: isVariantsLoading } =
    useGetVariantsByIds(variantIds);

  const defaultValues = useMemo<PackFormValues>(
    () => (pack ? getEditablePackFormValues(pack) : EMPTY_PACK_FORM_VALUES),
    [pack]
  );

  const methods = useForm<PackFormValues>({
    defaultValues,
    resolver: yupResolver(packSchema) as never,
    mode: "onSubmit"
  });

  useEffect(() => {
    if (!pack) return;
    const variantsById = new Map(hydratedVariants.map((v) => [v.id, v]));
    const values = getEditablePackFormValues(pack);
    values.slots = values.slots.map((slot) => ({
      ...slot,
      options: slot.options.map((option) => {
        const v = variantsById.get(option.productVariantId);
        const thumb = v?.preview ?? v?.compressedPreview;
        return {
          ...option,
          productVariantName: v?.name ?? option.productVariantName,
          productVariantImage: thumb ?? option.productVariantImage
        };
      })
    }));
    methods.reset(values);
  }, [pack, hydratedVariants]);

  const onSaveError = (error: Parameters<typeof toastServerError>[0]) => {
    if (!handlePackError(error as never)) {
      toastServerError(error);
    }
  };

  const handleSuccess = (message: string) => {
    toast.success(message);
    void refetchList();
    navigate("/packs");
  };

  const createMutation = useCreatePack({
    onSuccess: () => handleSuccess(t("Pack created")),
    onError: onSaveError
  });

  const updateMutation = useUpdatePack({
    onSuccess: (updated) => {
      if (isEdit) {
        replaceSlotsMutation.mutate({
          packId: updated.id,
          slots: convertPackFormDataToCreateRequestBody(methods.getValues())
            .slots
        });
      }
    },
    onError: onSaveError
  });

  const replaceSlotsMutation = useReplacePackSlots({
    onSuccess: () => handleSuccess(t("Pack updated")),
    onError: onSaveError
  });

  const onSubmit = (values: PackFormValues) => {
    if (isEdit) {
      updateMutation.mutate(
        convertPackFormDataToUpdateRequestBody(packId as string, values)
      );
    } else {
      createMutation.mutate(convertPackFormDataToCreateRequestBody(values));
    }
  };

  const isSubmitting =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    replaceSlotsMutation.isLoading;

  if (isEdit && (isPackLoading || isVariantsLoading)) return <LoaderScreen />;

  return (
    <ContentLayout
      title={isEdit ? t("Edit Pack") : t("New Pack")}
      action={
        <div style={{ display: "flex", gap: 12 }}>
          {isEdit && pack && (
            <Button
              variant="default"
              onClick={() =>
                toggleActiveMutation.mutate({
                  packId: pack.id,
                  isActive: !pack.isActive
                })
              }
              icon={
                pack.isActive ? (
                  <EyeOffIcon fill={"#090914"} width={20} height={20} />
                ) : (
                  <EyeIcon fill={"#090914"} width={20} height={20} />
                )
              }
            >
              {pack.isActive ? t("Hide") : t("Show")}
            </Button>
          )}
          <Button variant="default" onClick={() => navigate("/packs")}>
            {t("Back")}
          </Button>
        </div>
      }
    >
      <BoxShadowLayout>
        <FormProvider {...methods}>
          <form
            className={styles.packForm}
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <PackFormHeader isEdit={isEdit} />

            <div className={styles.packForm__line} />

            <PackSlotsEditor />

            <div className={styles.packForm__line} />

            <div className={styles.packForm__buttons}>
              <Button
                type="button"
                variant="default"
                onClick={() => navigate("/packs")}
              >
                {t("Cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isEdit ? t("Save changes") : t("Create pack")}
              </Button>
            </div>
          </form>
        </FormProvider>
      </BoxShadowLayout>
    </ContentLayout>
  );
};

export default PackDetails;
